import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { SIGN_IN_PATH } from "@/lib/auth-routes";
import { toast } from "sonner";
import { z } from "zod";
import { Loader2, Sparkles } from "lucide-react";
import { generatePassword, strongPasswordSchema } from "@/lib/password";
import { PasswordField } from "@/components/auth/PasswordField";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: strongPasswordSchema,
  fullName: z.string().min(1, "Please enter the customer's name").max(200),
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Super-admin only: creates an auth user with email confirmation off, then sends
 * a password reset email so the customer can set their password and sign in.
 */
export function CreateCustomerUserDialog({ open, onOpenChange }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async () => {
    const parsed = formSchema.safeParse({
      email,
      password,
      fullName: fullName.trim(),
    });
    if (!parsed.success) {
      toast.error(parsed.error.errors[0].message);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "admin-create-user",
        {
          body: {
            email: parsed.data.email,
            password: parsed.data.password,
            full_name: parsed.data.fullName,
          },
        },
      );

      if (fnError) {
        toast.error(fnError.message || "Failed to create user.");
        return;
      }

      if (data && typeof data === "object" && "error" in data && data.error) {
        toast.error(String(data.error));
        return;
      }

      const targetEmail = (data?.email as string) || parsed.data.email;

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        targetEmail,
        { redirectTo: `${window.location.origin}${SIGN_IN_PATH}` },
      );

      if (resetError) {
        toast.error(
          `User was created but the reset email could not be sent: ${resetError.message}`,
        );
        return;
      }

      toast.success(
        `Account created for ${targetEmail}. A password reset link was sent.`,
      );
      setEmail("");
      setPassword("");
      setFullName("");
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message || "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create customer account</DialogTitle>
          <DialogDescription>
            Creates the user. Only a password reset email is sent so they can
            choose a password and sign in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="create-customer-full-name">Full name</Label>
            <Input
              id="create-customer-full-name"
              type="text"
              autoComplete="off"
              placeholder="Customer name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void handleCreate()}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-customer-email">Email</Label>
            <Input
              id="create-customer-email"
              type="email"
              autoComplete="off"
              placeholder="customer@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void handleCreate()}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="create-customer-password">Initial password</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setPassword(generatePassword())}
              >
                <Sparkles className="mr-1 h-3.5 w-3.5" />
                Generate
              </Button>
            </div>
            <PasswordField
              id="create-customer-password"
              autoComplete="new-password"
              placeholder="Temporary password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void handleCreate()}
            />
            {password && (
              <PasswordRequirements
                value={password}
                columns={2}
                className="animate-in fade-in-0"
              />
            )}
            <p className="text-xs text-muted-foreground">
              Stored until they set a new password via the reset link.
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="gradient-primary"
            onClick={() => void handleCreate()}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating…
              </>
            ) : (
              "Create & send reset link"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
